import type Pusher from 'pusher-js';

import { defineStore } from 'pinia';
import { v4 as uuidv4 } from 'uuid';
import { markRaw } from 'vue';

import type { ActiveSubscription, PusherConfig } from '~/types/websocket';

import { PusherConnectionState } from '~/types/websocket';

export type { ActiveSubscription };

export const usePusherConnectionStore = defineStore('pusherConnection', () => {
  const { $logWarnMessage, $logInfoMessage } = useNuxtApp();
  // Pusher client instance (singleton)
  const pusherClient = ref<Pusher | null>(null);

  // Connection state
  const connectionState = ref<PusherConnectionState>(PusherConnectionState.Initialized);

  // Track if we were disconnected (to detect reconnections)
  const wasDisconnected = ref(false);

  // Reconnection callbacks
  const reconnectCallbacks = ref<Set<() => void | Promise<void>>>(new Set());

  // Active subscriptions (keyed by unique ID)
  const activeSubscriptions = ref<Map<string, ActiveSubscription>>(new Map());

  // Track in-flight initialization to prevent race conditions
  let initPromise: Promise<Pusher> | null = null;

  // Computed properties
  const isConnected = computed(() => connectionState.value === PusherConnectionState.Connected);

  // Actions
  const setPusherClient = (client: Pusher | null) => {
    // Mark as raw to prevent Vue from making Pusher internals reactive
    pusherClient.value = client ? markRaw(client) : null;
  };

  const setConnectionState = (state: PusherConnectionState) => {
    connectionState.value = state;
  };

  const setWasDisconnected = (value: boolean) => {
    wasDisconnected.value = value;
  };

  const addReconnectCallback = (callback: () => void | Promise<void>): (() => void) => {
    reconnectCallbacks.value.add(callback);
    return () => reconnectCallbacks.value.delete(callback);
  };

  const triggerReconnectCallbacks = () => {
    reconnectCallbacks.value.forEach(cb => cb());
  };

  const addSubscription = (subscription: ActiveSubscription) => {
    activeSubscriptions.value.set(subscription.id, subscription);
  };

  const removeSubscription = (id: string) => {
    activeSubscriptions.value.delete(id);
  };

  const updateSubscription = (id: string, updates: Partial<ActiveSubscription>) => {
    const sub = activeSubscriptions.value.get(id);
    if (sub) {
      activeSubscriptions.value.set(id, { ...sub, ...updates });
    }
  };

  const getSubscription = (id: string): ActiveSubscription | undefined => {
    return activeSubscriptions.value.get(id) as ActiveSubscription | undefined;
  };

  const hasSubscription = (id: string): boolean => {
    return activeSubscriptions.value.has(id);
  };

  const getAllSubscriptions = (): ActiveSubscription[] => {
    return Array.from(activeSubscriptions.value.values()) as ActiveSubscription[];
  };

  const getSubscriptionCount = (): number => {
    return activeSubscriptions.value.size;
  };

  const generateSubscriptionId = (): string => {
    return uuidv4();
  };

  const getInitPromise = (): Promise<Pusher> | null => {
    return initPromise;
  };

  const setInitPromise = (promise: Promise<Pusher> | null) => {
    initPromise = promise;
  };

  const initialize = async (config: PusherConfig): Promise<Pusher> => {
    // Return existing client if already initialized
    if (pusherClient.value) {
      return pusherClient.value as Pusher;
    }

    // Return existing promise if initialization is in progress
    if (initPromise) {
      return initPromise;
    }

    // Start initialization and store the promise
    initPromise = (async () => {
      const { default: PusherJS } = await import('pusher-js');
      const newPusherClient = new PusherJS(config.appKey, {
        ...config,
      });

      setPusherClient(newPusherClient);

      // Set initial connection state from Pusher client
      setConnectionState(newPusherClient.connection.state as PusherConnectionState);

      // Bind to Pusher's error event
      newPusherClient.connection.bind('error', (err: { type: string; error: { data?: { code?: number; message?: string } } }) => {
        $logWarnMessage('Pusher connection error', {
          errorType: err.type,
          errorCode: err.error?.data?.code,
          errorMessage: err.error?.data?.message,
          connectionState: newPusherClient.connection.state,
        });
      });

      // Bind to Pusher's built-in connection events
      newPusherClient.connection.bind('state_change', ({ previous, current }: { previous: PusherConnectionState; current: PusherConnectionState }) => {
        setConnectionState(current);

        // Log failed or unavailable states as errors
        if (current === PusherConnectionState.Failed || current === PusherConnectionState.Unavailable) {
          $logWarnMessage('Pusher connection failed', {
            previousState: previous,
            currentState: current,
            socketId: newPusherClient.connection.socket_id,
          });
        }

        // Connection lost
        if (previous === PusherConnectionState.Connected && current !== PusherConnectionState.Connected) {
          setWasDisconnected(true);
          $logInfoMessage('Pusher connection lost', {
            previousState: previous,
            currentState: current,
          });
        }

        // Reconnected after disconnect
        if (current === PusherConnectionState.Connected && wasDisconnected.value) {
          $logInfoMessage('Pusher reconnected after disconnect', {
            previousState: previous,
          });
          setWasDisconnected(false);
          triggerReconnectCallbacks();
        }
      });

      return newPusherClient;
    })();

    return initPromise;
  };

  return {
    // State
    pusherClient,
    connectionState,
    isConnected,
    activeSubscriptions,

    // Actions
    setPusherClient,
    setConnectionState,
    setWasDisconnected,
    addReconnectCallback,
    triggerReconnectCallbacks,
    addSubscription,
    removeSubscription,
    updateSubscription,
    getSubscription,
    hasSubscription,
    getAllSubscriptions,
    getSubscriptionCount,
    generateSubscriptionId,
    getInitPromise,
    setInitPromise,
    initialize,
  };
});
